import { memo, useState, useMemo, useCallback } from 'react';
import { HStack, VStack } from '@cbhq/cds-web/layout';
import { TextDisplay3, TextHeadline, TextTitle4 } from '@cbhq/cds-web/typography';
import { SegmentedTabs } from '@cbhq/cds-web/tabs/SegmentedTabs';
import { IconButton } from '@cbhq/cds-web/buttons/IconButton';
import {
  XYChart,
  AnimatedLineSeries,
  AnimatedAxis,
  AnimatedGrid,
  AnimatedAreaSeries,
  buildChartTheme,
} from '@visx/xychart';
import { useParentSize } from '@visx/responsive';
import { DisplayType, format } from '@/utils/numberFormatter';
import { TimeRangeSelector, type TimeRange } from '@/components/common/TimeRangeSelector';
import { PoolDetailsData, PriceDataPoint } from './hooks/usePoolDetailsData';

type ChartTab = 'price' | 'volume' | 'liquidity';

const chartTabs = [
  { id: 'price', label: 'Price' },
  { id: 'volume', label: 'Volume' },
  { id: 'liquidity', label: 'Liquidity' },
];

type Props = {
  pool: PoolDetailsData;
  priceHistory: PriceDataPoint[];
};

export const PoolDetailsChart = memo(function PoolDetailsChart({ pool, priceHistory }: Props) {
  const { parentRef, width, height } = useParentSize();
  const [activeTab, setActiveTab] = useState<ChartTab>('price');
  const [activeTimeRange, setActiveTimeRange] = useState<TimeRange>('1D');

  const handleTabChange = useCallback((tab: { id: string } | null) => {
    if (tab) {
      setActiveTab(tab.id as ChartTab);
    }
  }, []);

  const handleTimeRangeChange = useCallback((range: TimeRange) => {
    setActiveTimeRange(range);
  }, []);

  // Get display values based on active tab
  const displayValue = useMemo(() => {
    switch (activeTab) {
      case 'price':
        return `1 ${pool.baseSymbol} = ${format({
          value: pool.currentPrice,
          type: DisplayType.TokenPrice,
        })} ${pool.quoteSymbol}`;
      case 'volume':
        return format({ value: pool.volume24h, type: DisplayType.FiatShort });
      case 'liquidity':
        return format({ value: pool.tvl, type: DisplayType.FiatShort });
      default:
        return '';
    }
  }, [activeTab, pool]);

  const changeValue = useMemo(() => {
    switch (activeTab) {
      case 'price':
        return pool.priceChange;
      case 'volume':
        return pool.volume24hChange;
      case 'liquidity':
        return pool.tvlChange;
      default:
        return 0;
    }
  }, [activeTab, pool]);

  const isPositiveChange = changeValue >= 0;

  // Chart theme
  const theme = useMemo(
    () =>
      buildChartTheme({
        backgroundColor: 'transparent',
        colors: ['#CB51BB'],
        gridColor: '#eef0f3',
        gridColorDark: '#DDD',
        tickLength: 4,
      }),
    [],
  );

  // Chart accessors
  const accessors = useMemo(
    () => ({
      xAccessor: (d: PriceDataPoint) => d.date,
      yAccessor: (d: PriceDataPoint) => d.value,
    }),
    [],
  );

  // Calculate Y-axis domain
  const yDomain = useMemo(() => {
    if (priceHistory.length === 0) return [0, 1];

    const values = priceHistory.map((d) => d.value);
    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);

    if (minValue === maxValue) {
      const padding = Math.max(minValue * 0.1, 0.1);
      return [Math.max(0, minValue - padding), maxValue + padding];
    }

    const range = maxValue - minValue;
    const padding = range * 0.1;
    return [Math.max(0, minValue - padding), maxValue + padding];
  }, [priceHistory]);

  // Date formatter for x-axis
  const dateFormatter = useCallback((date: unknown) => {
    if (!(date instanceof Date)) return '';
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }, []);

  const currentActiveTab = chartTabs.find((t) => t.id === activeTab) ?? chartTabs[0];

  return (
    <VStack background="bg" borderRadius={400} padding={3} gap={2}>
      {/* Header row with tabs and time range */}
      <HStack justifyContent="space-between" alignItems="center" width="100%">
        <SegmentedTabs tabs={chartTabs} activeTab={currentActiveTab} onChange={handleTabChange} />
        <TimeRangeSelector
          activeTimeRange={activeTimeRange}
          onTimeRangeChange={handleTimeRangeChange}
        />
      </HStack>

      {/* Price display section */}
      <VStack alignItems="flex-start" gap={0.5}>
        <TextHeadline as="span" color="fgMuted">
          Past day
        </TextHeadline>
        <HStack alignItems="center" gap={1}>
          <TextDisplay3 as="span">{displayValue}</TextDisplay3>
          <IconButton name="arrowsHorizontal" variant="secondary" compact />
        </HStack>
        <TextTitle4 as="span" color={isPositiveChange ? 'fgPositive' : 'fgNegative'}>
          {isPositiveChange ? '↗' : '↘'}{' '}
          {format({ value: Math.abs(changeValue / 100), type: DisplayType.Percentage })}
        </TextTitle4>
      </VStack>

      {/* Chart area */}
      <div style={{ width: '100%', height: 350 }} ref={parentRef}>
        {priceHistory.length > 0 && width > 0 && height > 0 && (
          <XYChart
            height={height}
            width={width}
            xScale={{ type: 'time' }}
            yScale={{ type: 'linear', domain: yDomain }}
            theme={theme}
            margin={{ left: 2, top: 10, bottom: 30, right: 2 }}
          >
            <AnimatedAxis orientation="bottom" numTicks={6} tickFormat={dateFormatter} />
            <AnimatedGrid columns={false} rows numTicks={5} />

            <defs>
              <pattern id="dot-pattern-explore" patternUnits="userSpaceOnUse" width="4" height="4">
                <circle cx="2" cy="2" r="0.7" fill="#CB51BB" fillOpacity="0.3" />
              </pattern>
            </defs>

            <AnimatedAreaSeries
              dataKey="Price"
              data={priceHistory}
              xAccessor={accessors.xAccessor}
              yAccessor={accessors.yAccessor}
              fill="url(#dot-pattern-explore)"
              fillOpacity={1}
              stroke="none"
            />
            <AnimatedLineSeries
              dataKey="Price"
              data={priceHistory}
              xAccessor={accessors.xAccessor}
              yAccessor={accessors.yAccessor}
            />
          </XYChart>
        )}
      </div>
    </VStack>
  );
});
